
import { useEffect, useState } from 'react';
import { useOrderContext } from '../../context/OrderContext';
import { useUserContext } from '../../context/UserContext';
import { useCartContext } from '../../context/CartContext';
import Link from '@material-ui/core/Link';
import { KeyTypes, searchActionTypes } from '../../app/enums';
import { DataGrid, GridRowsProp, GridColDef, GridCellParams } from '@mui/x-data-grid';
import { Typography, Grid, Button, IconButton } from '@material-ui/core';
import dateFormat from 'dateformat';
import NumberFormat from 'react-number-format';
import { useSearchContext } from '../../context/SearchContext';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCart';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { EditSearchDialog } from '../../components/Dialogs/EditSearchDialog';
import { Alert } from '@material-ui/lab';
import { Searches } from '../../api/api';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    alertNoMargin: {
      width: '100%',
    },
    gridSpacer: {
      minHeight: '12px',
    },
    dataGridRoot:{
      border: 0,
      '& .header' : {
        background: theme.palette.background.default,
        color: theme.palette.primary.dark,
        fontWeight: '600', 
        fontSize: 16,
        padding: theme.spacing(0),
      },
      '& .MuiDataGrid-menuIconButton': {
        color: theme.palette.primary.dark,
        size: 'small',
        padding: theme.spacing(0),
      },
      '& .MuiDataGrid-sortIcon': {
        color: theme.palette.primary.dark,
        size: 'small',
        padding: theme.spacing(0),
      },
      headerHeight: 45,
      rowHeight: 40,
      pageSize: 10,
    },
    card: {
      marginLeft: theme.spacing(1),
      width: '100%'
    },
  }),
);

interface ISearchHistoryGrid {
  onOpen: () => void;
}

export const SearchHistoryGrid = ({ onOpen } : ISearchHistoryGrid) => {
  const classes = useStyles(); 
  const { state, dispatch } = useSearchContext();
  const order = useOrderContext();
  const user = useUserContext();
  const cart = useCartContext();
  const [ listLoading, setListLoading ] = useState(false);
  const [ searchInCart, setSearchInCart ] = useState<string[]>([]) 
  const [ edit, setEdit ] = useState<boolean>(false); 
  const [ editSearchId, setEditSearchId ] = useState<string>('')
  const [ editField, setEditField ] = useState<string>('')
  const [ editLot, setEditLot ] = useState<string>('')
  const [ editSeller, setEditSeller ] = useState<string>('')
  const [ error, setError ] = useState<string>('')
  const [ page, setPage ] = useState(0)

  function loadSearchList(){
    setListLoading(true)
    Searches.getSearches(user.state.self.locations[0].id)
      .then((searchList) => {   
        const searchGrid: SearchLine[] = searchList.map((search) => {
          const hasSearchId = state.searchOrderMap.has(search.id);
          
          return {
            search: search,
            order: hasSearchId ?  state.searchOrderMap.get(search.id) : undefined,
          }
        });

        if(error.length > 0){
          setError(error)
        }

        dispatch({ type: searchActionTypes.LOAD_SEARCH_LIST, searchList: searchGrid})
        setListLoading(false) 
      }).catch((error) => {
        setError(error)
      }) 
  }

  useEffect(() => {
    loadSearchList()
    let tempSearchInCart: string[] = [];

    cart.state.cartItems.forEach((cartItem) => {
      tempSearchInCart.push(cartItem.searchId)
    })
    
    setSearchInCart(tempSearchInCart)
  },[state.searchOrderMap, cart.state, edit])

  const handleButtonClick = (orderId: string) => {

    order.dispatch({type: 'field', fieldName: 'orderId', payload:orderId})
    onOpen()
  };

  const handleAddToCart = (searchId: string | undefined) => {
    Searches.getSearch(searchId || '')
      .then((search) => {
        dispatch({ 
          type: searchActionTypes.SHOW_ADD_TO_CART, 
          vin: search.vin,
          item: search
        })
      })
  }

  const columns: GridColDef[] = [
    { 
      field: 'vin', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'VIN', 
      flex: 1.25
    },
    { 
      field: 'vehicle', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'Vehicle', 
      flex: 1.25,
      renderCell: (params: GridCellParams) => (
        <Typography variant="inherit" display="inline">  
          {params.value} {params.getValue(params.id, 'make')} {params.getValue(params.id, 'model')}
        </Typography>
      )
    },
    { 
      field: 'keyType',
      headerClassName: 'header', 
      headerAlign: 'center', 
      headerName: 'Type', 
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Typography variant="inherit" display="inline">  
          {KeyTypes[params.value as keyof typeof KeyTypes]}
        </Typography>     
      )
    },
    { 
      field: 'price',
      headerClassName: 'header', 
      headerAlign: 'center', 
      headerName: 'Price', 
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <NumberFormat 
          value={params.value + ''}
          displayType={'text'} 
          thousandSeparator={true} 
          prefix={'$'} 
          decimalScale={2}
          fixedDecimalScale={true}
        />  
      )
    },
    { 
      field: 'lotNumber',
      headerClassName: 'header', 
      headerAlign: 'center', 
      headerName: 'Lot #', 
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Grid container 
            justifyContent="center" 
            alignItems="center" 
          >
            {params.value 
              ? <Link 
                  onClick = {() => showEditDialog(params)}>
                  {params.value}
                </Link>
              : <IconButton
                  aria-label="editLotNumber"
                  onClick={() => showEditDialog(params)}
                  color="primary"
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
            }   
        </Grid>
      ),
    },
    { 
      field: 'seller',
      headerClassName: 'header', 
      headerAlign: 'center', 
      headerName: 'Seller', 
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Grid container 
            justifyContent="center" 
            alignItems="center" 
          >
            {params.value 
              ? <Link 
                  onClick = {() => showEditDialog(params)}>
                  {params.value}
                </Link>
              : <IconButton
                  aria-label="editSeller"
                  onClick={() => showEditDialog(params)}
                  color="primary"
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
            }   
        </Grid>
      ),
    },
    { 
      field: 'searchedAt', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'Date', 
      flex: 1,
      renderCell: (params: GridCellParams) => (
        params.value !== undefined && params.value !== null
        ?
          <Typography variant="inherit" display="inline">  
            {dateFormat(new Date(params.value + ''), "mmm dd, yyyy")}  
          </Typography>
        : ''
      )
    },
    { 
      field: 'order', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'Order #', 
      flex: 1,
      renderCell: (params: GridCellParams) => (
        params.getValue(params.id, 'orderNumber') !== undefined 
        ? getOrderLink(params)
        : getAddToCart(params)
      ),
    },
  ];

  const getOrderLink = (params: GridCellParams) => {
    return (
      params.getValue(params.id, 'orderNumber') !== null 
        && (params.getValue(params.id, 'orderNumber') + '').length > 0
      ? <Link 
          onClick = {() => handleButtonClick(params.getValue(params.id, 'orderId') + '')}>
          {params.getValue(params.id, 'orderNumber')}
        </Link>
      : <Link 
          onClick = {() => handleButtonClick(params.getValue(params.id, 'orderId') + '')}>
          {params.getValue(params.id, 'orderId')}
        </Link>
    )
  }

  const getAddToCart = (params: GridCellParams) => {
    let searchId: string = params.id ? params.id + '' : '';

    return (
      <Button 
        variant="outlined"
        color="primary"
        size="small" 
        startIcon={<AddShoppingCartIcon/>}
        onClick={() => handleAddToCart(params.getValue(params.id, 'id')?.toString()|| undefined)}
        disabled={searchInCart.indexOf(searchId) > -1}
      >
        Add
      </Button> 
    )
  }

  const showEditDialog = (params: GridCellParams) => {
    setEditSearchId(params.row.id)
    setEditField(params.field)
    setEditLot(params.row.lotNumber)
    setEditSeller(params.row.seller)
    setEdit(true)
  }

  const handleEditClose = () => {
    setEdit(false);
  };

  const rows: GridRowsProp = state.searchList.map((item) => ({
    id: item.search.id,
    vin: item.search.vin,
    vehicle: item.search.year + ' ' + item.search.make + ' ' + item.search.model,
    keyType: item.search.keyType,
    searchedAt: item.search.searchedAt,
    price: item.search.price,
    order: item.order === undefined ? undefined : item.order.orderNumber, 
    orderId: item.order === undefined ? undefined : item.order.id,
    orderNumber: item.order === undefined ? undefined : item.order.orderNumber,
    lotNumber: item.search.lotNumber || '',
    seller: item.search.sellerName || ''
  }));

  return (
    <>
      {error.length > 0   &&    
        <Grid container>
          <Alert severity="error" className={classes.alertNoMargin} >
            {error}
          </Alert>   
          <Grid className={classes.gridSpacer} />
        </Grid>
      } 
      <Grid className={classes.gridSpacer} />
      <div style={{ height: 500, width: '1250px' }} className={classes.dataGridRoot}>
        { listLoading 
          ? <Grid container 
              justifyContent="center"
              alignItems="center"
            >
              <CircularProgress color="primary" />
            </Grid>
          : <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowHeight={40}
              headerHeight={45}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
            />
        }
      </div>
      <EditSearchDialog
        open= {edit}
        onClose= {handleEditClose}
        searchId= {editSearchId}
        fieldName= {editField}
        lotNumber= {editLot}
        sellerName= {editSeller}
      />
    </>
  )
};

export default SearchHistoryGrid;
